import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { AdjuntosTab } from '@/components/domain/adjuntos-tab';

interface CertificadosModalProps {
  ticketId: number;
  correlativo: string;
  onClose: () => void;
}

/**
 * Lists a ticket's attached certificates so they can be viewed straight from a
 * list, without drilling into the ticket detail.
 *
 * The main-app twin of `components/miniapp/certificados-modal`. Same gesture,
 * same wording, same artifact (`ot_certificado`) — a coordinator who uses the
 * miniapp for a lote and the platform for a past month should not have to
 * learn two vocabularies for the same document. It differs only in its data
 * source: the miniapp reads the lote-scoped endpoint, this one reuses
 * `AdjuntosTab` (readonly), which already renders the Ver/Descargar cards from
 * `/tickets/:id/adjuntos` and is the single source of truth for that markup.
 */
export function CertificadosModal({ ticketId, correlativo, onClose }: CertificadosModalProps) {
  // Escape closes — the badge that opens this lives inside a clickable row, so
  // the keyboard path out must not depend on hitting the small X.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Certificados de ${correlativo}`}
      className="fixed inset-0 z-[400] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-sime-surface shadow-xl"
        onClick={(e) => { e.stopPropagation(); }}
      >
        <div className="flex items-center justify-between border-b border-sime-border-subtle px-4 py-3">
          <span className="truncate text-sm font-semibold text-sime-text-primary">
            {correlativo} — Certificados
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar certificados"
            className="ml-3 shrink-0 rounded-lg p-1 text-sime-text-secondary hover:bg-sime-surface-raised"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-3">
          <AdjuntosTab ticketId={ticketId} readonly />
        </div>
      </div>
    </div>,
    document.body,
  );
}
